import React, { useCallback, useMemo } from 'react';
import { FormInput, FormTextArea, GridTabs } from '@ihsm/ui-shared';
import { MetricParameter, MetricType } from 'services/types';
import { ParametersSelection } from './ParametersSelection';
import { MetricsDraggableProps, MetricsForm } from '../types';
import './NewMetricsForm.scss';

import { ToggleTypeDefaultValues } from '../mocks';

export interface NewMetricsFormProps {
  form?: MetricsForm;
  setForm?: (form: MetricsForm) => void;
  disabled?: boolean;
}

export const NewMetricsForm: React.FC<NewMetricsFormProps> = ({
  form,
  setForm = () => {},
  disabled = false,
}) => {
  const metricsParameters: MetricsDraggableProps[] = useMemo(() => {
    if (!form?.parameters || form.parameters.length <= 0) return [];
    return form.parameters.map((p) => ({
      text: p.name,
      type: p.category,
      selectedOption: p.category,
    }));
  }, [form?.parameters]);

  const onChangeMetricsData = useCallback(
    (list: MetricsDraggableProps[]) => {
      if (form) {
        const parameters: MetricParameter[] = list.map((p) => ({
          name: p.text,
          category: p.selectedOption || p.type,
        }));

        setForm({ ...form, parameters });
      }
    },
    [form, setForm]
  );

  const onChangeTab = useCallback(
    (option: string) => {
      if (form) {
        const type = option as MetricType;
        setForm({ ...form, type });
      }
    },
    [form, setForm]
  );

  if (form === undefined) {
    return <div>No metrics available.</div>;
  }

  return (
    <div className="new-metric-form">
      <FormInput
        label="Name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        disabled={disabled}
      />
      <FormTextArea
        label="Description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        disabled={disabled}
      />
      <GridTabs
        tabs={ToggleTypeDefaultValues}
        activeTab={form.type}
        label="Type"
        onChange={onChangeTab}
        disabledTabs={disabled ? ToggleTypeDefaultValues : undefined}
      />
      <FormTextArea
        label="Definition"
        value={form.definition}
        onChange={(e) => setForm({ ...form, definition: e.target.value })}
        disabled={disabled}
        minHeight={250}
      />

      {form.type === 'VaR' ? (
        <ParametersSelection
          data={metricsParameters}
          onDataChange={onChangeMetricsData}
          disabled={disabled}
        />
      ) : null}
    </div>
  );
};
